/**
 * useNetworkStatus - Centralized Network Status Management
 * 
 * Provides reliable offline detection with 3-level protection:
 * 1. Browser online/offline events (navigator.onLine)
 * 2. Sync service status (EventBus)
 * 3. Periodic backend health checks (ACTIVE PING every 3s)
 * 
 * This is the SINGLE SOURCE OF TRUTH for network status.
 */

import { useState, useEffect } from 'react';
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { logger } from '@/services/logger';

export interface NetworkStatus {
  isOnline: boolean;
  isConnectedToBackend: boolean;
  lastCheckTime: number;
  connectionQuality: 'good' | 'poor' | 'offline';
}

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isConnectedToBackend: true,
    lastCheckTime: 0, // 0 = initial mount, will be updated by first ping
    connectionQuality: navigator.onLine ? 'good' : 'offline',
  });

  useEffect(() => {
    // =========================================================================
    // Level 1: Browser online/offline events
    // =========================================================================
    const handleOnline = () => {
      logger.info('[useNetworkStatus] Browser detected ONLINE', undefined, 'useNetworkStatus');
      setStatus((prev) => ({
        ...prev,
        isOnline: true,
        lastCheckTime: Date.now(),
      }));
      // Immediately verify with backend ping
      checkBackendConnection();
    };

    const handleOffline = () => {
      logger.warn('[useNetworkStatus] Browser detected OFFLINE', undefined, 'useNetworkStatus');
      setStatus((prev) => ({
        ...prev,
        isOnline: false,
        isConnectedToBackend: false,
        connectionQuality: 'offline',
        lastCheckTime: Date.now(),
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // =========================================================================
    // Level 2: Sync service events
    // =========================================================================
    const handleSyncFailed = (data?: any) => {
      logger.warn(
        '[useNetworkStatus] Sync service reported connection LOST',
        { reason: data?.reason },
        'useNetworkStatus'
      );
      setStatus((prev) => ({
        ...prev,
        isConnectedToBackend: false,
        connectionQuality: 'offline',
        lastCheckTime: Date.now(),
      }));
    };

    const handleSyncCompleted = () => {
      logger.info('[useNetworkStatus] Sync service reported connection RESTORED', undefined, 'useNetworkStatus');
      setStatus((prev) => ({
        ...prev,
        isOnline: true,
        isConnectedToBackend: true,
        connectionQuality: 'good',
        lastCheckTime: Date.now(),
      }));
    };

    const handleSyncStarted = () => {
      logger.debug('[useNetworkStatus] Sync started - connection seems good', undefined, 'useNetworkStatus');
      setStatus((prev) => ({
        ...prev,
        isConnectedToBackend: true,
        connectionQuality: prev.isOnline ? 'good' : 'poor',
        lastCheckTime: Date.now(),
      }));
    };

    const unsubscribeSyncFailed = eventBus.on(EVENT_TYPES.SYNC_FAILED, handleSyncFailed);
    const unsubscribeSyncCompleted = eventBus.on(EVENT_TYPES.SYNC_COMPLETED, handleSyncCompleted);
    const unsubscribeSyncStarted = eventBus.on(EVENT_TYPES.SYNC_STARTED, handleSyncStarted);

    // =========================================================================
    // Level 3: PERIODIC BACKEND HEALTH CHECK (ACTIVE PING)
    // =========================================================================
    const checkBackendConnection = async () => {
      // First check browser online status (lightweight)
      if (!navigator.onLine) {
        setStatus((prev) => {
          // Only update if state actually changed
          if (prev.isOnline || prev.isConnectedToBackend) {
            logger.warn('[useNetworkStatus] Browser offline, updating state', undefined, 'useNetworkStatus');
            return {
              isOnline: false,
              isConnectedToBackend: false,
              connectionQuality: 'offline' as const,
              lastCheckTime: Date.now(),
            };
          }
          return prev;
        });
        return;
      }

      // Use AbortController for timeout control
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 2000); // 2s timeout

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (!supabaseUrl) {
          clearTimeout(timeoutId);
          logger.error('[useNetworkStatus] Missing VITE_SUPABASE_URL', undefined, 'useNetworkStatus');
          return;
        }

        const healthUrl = `${supabaseUrl}/rest/v1/`;
        const response = await fetch(healthUrl, {
          method: 'HEAD',
          cache: 'no-cache',
          signal: abortController.signal,
          headers: {
            apikey: supabaseAnonKey || '',
            Authorization: `Bearer ${supabaseAnonKey || ''}`,
          },
        });

        clearTimeout(timeoutId);

        // Connection is good (404 is acceptable - means Supabase is reachable)
        if (response.ok || response.status === 404 || response.status < 500) {
          setStatus((prev) => {
            // Only update if state actually changed
            if (!prev.isConnectedToBackend || prev.connectionQuality !== 'good') {
              logger.info('[useNetworkStatus] Backend connection RESTORED', undefined, 'useNetworkStatus');
              return {
                isOnline: true,
                isConnectedToBackend: true,
                connectionQuality: 'good' as const,
                lastCheckTime: Date.now(),
              };
            }
            return { ...prev, lastCheckTime: Date.now() };
          });
        } else {
          // Server error but network is up
          setStatus((prev) => ({
            ...prev,
            isOnline: true,
            connectionQuality: 'poor' as const,
            lastCheckTime: Date.now(),
          }));
        }
      } catch (error: any) {
        clearTimeout(timeoutId);

        // Network error, timeout, or abort - we're offline
        setStatus((prev) => {
          // Only update if state actually changed
          if (prev.isConnectedToBackend || prev.connectionQuality !== 'offline') {
            // Don't spam logs with every failed ping
            if (error?.name !== 'AbortError' && !abortController.signal.aborted) {
              logger.warn(
                '[useNetworkStatus] Backend connection LOST',
                { errorType: error?.name, message: error?.message },
                'useNetworkStatus'
              );
            }
            return {
              ...prev,
              isOnline: false,
              isConnectedToBackend: false,
              connectionQuality: 'offline' as const,
              lastCheckTime: Date.now(),
            };
          }
          return { ...prev, lastCheckTime: Date.now() };
        });
      }
    };

    // Initial connection check
    checkBackendConnection();

    // Set up active ping every 3 seconds
    const pingInterval = setInterval(checkBackendConnection, 3000);

    // =========================================================================
    // Cleanup
    // =========================================================================
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(pingInterval);
      unsubscribeSyncFailed();
      unsubscribeSyncCompleted();
      unsubscribeSyncStarted();
    };
  }, []); // Empty deps - setup once, cleanup on unmount

  return status;
}
